import React from "react";
import {
  DocumentPdf20Filled,
  Document20Filled,
  SlideText20Filled,
  DocumentTable20Filled,
  Image20Filled,
  DocumentText20Filled,
  Document20Regular,
} from "@fluentui/react-icons";
import styles from "./FileChips.module.css";

// ─── File type detection ────────────────────────────────────────────────────

type FileType = "pdf" | "word" | "pptx" | "excel" | "image" | "text" | "other";

function getFileType(filename: string): FileType {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "word";
  if (["ppt", "pptx"].includes(ext)) return "pptx";
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext))
    return "image";
  if (["txt", "md", "rtf"].includes(ext)) return "text";
  return "other";
}

function isImageFile(filename: string): boolean {
  return getFileType(filename) === "image";
}

// ─── Icon + color mapping ───────────────────────────────────────────────────

const FILE_TYPE_CONFIG: Record<
  FileType,
  {
    Icon: React.ComponentType<{ primaryFill?: string }>;
    color: string;
    bg: string;
  }
> = {
  pdf: { Icon: DocumentPdf20Filled, color: "#d92d20", bg: "#fef3f2" },
  word: { Icon: Document20Filled, color: "#2b6cb0", bg: "#eff6ff" },
  pptx: { Icon: SlideText20Filled, color: "#c4320a", bg: "#fff6ed" },
  excel: { Icon: DocumentTable20Filled, color: "#217346", bg: "#f0fdf4" },
  image: { Icon: Image20Filled, color: "#7c3aed", bg: "#f5f3ff" },
  text: { Icon: DocumentText20Filled, color: "#64748b", bg: "#f8fafc" },
  other: { Icon: Document20Regular, color: "#94a3b8", bg: "#f8fafc" },
};

// ─── Inline icons ───────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M3 3L9 9M9 3L3 9" />
  </svg>
);

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FileChipItem {
  name: string;
  uploading?: boolean;
}

interface FileChipsProps {
  files: FileChipItem[];
  /** Optional: base64 or object-URL thumbnails keyed by filename */
  thumbnails?: Record<string, string>;
  onRemove: (filename: string) => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

const FileChips: React.FC<FileChipsProps> = ({
  files,
  thumbnails = {},
  onRemove,
}) => {
  if (files.length === 0) return null;

  return (
    <div className={styles.container}>
      {files.map((file) => {
        const { name, uploading } = file;
        const type = getFileType(name);
        const thumb = thumbnails[name];
        const showThumb = isImageFile(name) && thumb && !uploading;
        const { Icon, color, bg } = FILE_TYPE_CONFIG[type];

        // Truncate long filenames
        const lastDot = name.lastIndexOf(".");
        const displayName =
          name.length > 28
            ? name.slice(0, 22) + "…" + (lastDot > 0 ? name.slice(lastDot) : "")
            : name;

        return (
          <div
            key={name}
            className={`${styles.chip} ${uploading ? styles.chipUploading : ""}`}
          >
            {/* Icon / thumbnail area */}
            <span className={styles.iconArea}>
              {showThumb ? (
                <img src={thumb} alt={name} className={styles.thumbnail} />
              ) : (
                <span className={styles.iconWrapper} style={{ background: bg }}>
                  <Icon primaryFill={color} />
                </span>
              )}
              {uploading && (
                <span className={styles.spinnerOverlay}>
                  <span className={styles.spinner} />
                </span>
              )}
            </span>

            <span className={styles.fileName} title={name}>
              {displayName}
            </span>

            <button
              className={styles.removeBtn}
              onClick={() => onRemove(name)}
              aria-label={`Remove ${name}`}
            >
              <CloseIcon />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default FileChips;
